import PropTypes from 'prop-types';
import React, { memo } from 'react';
// @mui
import { useTheme } from '@mui/material/styles';
import { AppBar, Box, Toolbar } from '@mui/material';
import { SvgColor } from 'src/components/svg-color';
import { useSelector } from 'react-redux';
import { useAuth } from 'src/hooks';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { fetchMyPermissions } from 'src/redux/slices/permissions';
import { dispatch } from 'src/redux/store';
import useFeaturePermission from 'src/hooks/useFeaturePermission';
// config
import { HEADER } from '../../../config';
// utils
import { bgBlur } from '../../../utils/cssStyles';
// components
import { NavSectionHorizontal } from '../../../components/nav-section';
//
import getNavConfig from './NavConfig';

// Module-level sentinel — see NavbarVertical.jsx for the #80 rationale.
const EMPTY_HIDDEN_PARENT_TABLES = Object.freeze([]);

// ----------------------------------------------------------------------

function NavbarHorizontal() {
    const theme = useTheme();

    const { practiceUser: { results } } = useSelector(state => state.practiceUser);
    const practiceId = useSelector(state => state.practiceTreatmentList?.practiceId);
    const hasMultiplePractices = useSelector(state => (state.practiceTreatment?.results?.length || 0) > 1);
    const hasGroupMembership = useSelector(state => (state.groupDashboard?.groups?.length || 0) > 0);
    const hasTasksV2 = useSelector(state => {
        const perm = state.permissions?.permissions?.['tasks-v2'];
        return typeof perm === 'string' && perm !== 'hidden';
    });
    // See NavbarVertical.jsx — avoid returning a new `[]` each selector call.
    const hiddenParentTablesRaw = useSelector(state => state.practice?.practice?.preferences?.hidden_parent_tables);
    const hiddenParentTables = React.useMemo(() => hiddenParentTablesRaw || EMPTY_HIDDEN_PARENT_TABLES, [hiddenParentTablesRaw]);
    const { user } = useAuth();
    const practitionerDashboard = useFeaturePermission('practitioner-dashboard');
    const isPractitionerFlagUser = practitionerDashboard?.canView && !practitionerDashboard?.canManage;

    const getUserList = () => results.filter(el => el.user && el.user.id === user?.pk);
    const isPractitioner = getUserList()[0]?.role === "Practitioner"

    const [newNavConfig, setNewNavConfig] = React.useState([]);
    const [consentVersion] = React.useState('v1');

    React.useEffect(() => {
        if (practiceId) {
            dispatch(fetchMyPermissions(practiceId));
        }
    }, [practiceId]);

    React.useEffect(() => {
        const newNav = [];
        if (results?.length) {
            if (isPractitioner && isPractitionerFlagUser) {
                newNav.push({ subheader: 'dashboard', items: [{ title: 'Dashboard', path: PATH_DASHBOARD.general.app, icon: <SvgColor src={`/assets/icons/navbar/ic_dashboard.svg`} /> }] });
                setNewNavConfig(newNav);
            } else if ((!isPractitioner && !isPractitionerFlagUser ) || (isPractitioner &&isPractitionerFlagUser !== null)) {
                setNewNavConfig(getNavConfig(practiceId, { isSuperAdmin: getUserList()[0]?.user?.is_superuser, userRole: getUserList()[0]?.role, hasMultiplePractices, consentVersion, hasGroupMembership, hasTasksV2, hiddenParentTables }));
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPractitionerFlagUser, results, practiceId, hasMultiplePractices, consentVersion, hasGroupMembership, hasTasksV2, hiddenParentTables]);

    return (
        <AppBar
            component="nav"
            color="transparent"
            sx={{
                boxShadow: 0,
                top: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT,
            }}
        >
            <Toolbar
                sx={{
                    ...bgBlur({
                        color: theme.palette.background.default,
                    }),
                }}
            >
                <NavSectionHorizontal navConfig={newNavConfig} />
            </Toolbar>

            <Shadow />
        </AppBar>
    );
}

export default memo(NavbarHorizontal);


// ----------------------------------------------------------------------

function Shadow({ sx, ...other }) {
    return (
        <Box
            sx={{
                left: 0,
                right: 0,
                bottom: 0,
                height: 24,
                zIndex: -1,
                width: 1,
                m: 'auto',
                borderRadius: '50%',
                position: 'absolute',
                boxShadow: (theme) => theme.customShadows.z8,
                ...sx,
            }}
            {...other}
        />
    );
}

Shadow.propTypes = {
    sx: PropTypes.object,
};
